import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { UploadIcon, FileIcon, CheckCircleIcon } from 'lucide-react';
import { formatFileSize } from '@/utils/fileUtils';
import { useImportTranslations } from '@/hooks/useImportTranslations';

interface UploadProgressProps {
  fileName: string;
  fileSize: number;
  uploadProgress: number;
  isUploading: boolean;
}

const UploadProgress: React.FC<UploadProgressProps> = ({
  fileName,
  fileSize,
  uploadProgress,
  isUploading,
}) => {
  const isComplete = uploadProgress >= 100 && !isUploading;

  // Initialize translations
  const { getProgressText, t } = useImportTranslations();

  return (
    <Card className="mx-auto w-full max-w-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {isComplete ? (
            <CheckCircleIcon className="size-5 text-green-500" />
          ) : (
            <UploadIcon className="size-5 text-blue-500" />
          )}
          {isComplete ? getProgressText('uploadComplete') : getProgressText('uploading')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-3 rounded-lg bg-gray-50 p-4">
          <FileIcon className="mt-1 size-8 shrink-0 text-blue-500" />
          <div className="min-w-0 flex-1">
            <p className="truncate font-medium text-gray-900" title={fileName}>
              {fileName}
            </p>
            <p className="text-sm text-gray-500">{formatFileSize(fileSize)}</p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {isComplete
                ? getProgressText('uploadComplete')
                : isUploading
                  ? getProgressText('uploading')
                  : getProgressText('preparing')}
            </span>
            <span className="text-sm font-medium text-gray-900">{uploadProgress}%</span>
          </div>

          <Progress value={uploadProgress} className="h-3 w-full" />

          {isUploading && uploadProgress < 100 && (
            <p className="text-center text-xs text-blue-600">
              📡 {getProgressText('uploadingFile')}
            </p>
          )}

          {isComplete && (
            <p className="text-center text-xs font-medium text-green-600">
              ✅ {getProgressText('uploadSuccessful')}
            </p>
          )}

          {uploadProgress === 100 && isUploading && (
            <p className="text-center text-xs text-blue-600">
              🔄 {getProgressText('processingFile')}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default UploadProgress;
